(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-pw-predictive-search]';
  const MIN_QUERY_LENGTH = 2;
  const INDEX_QUERY_LENGTH = 4;
  const RESULT_LIMIT = 10;
  const DEBOUNCE_DELAY = 180;
  const CACHE_LIMIT = 30;

  const normalizeIdentifier = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const isCompactQuery = (query) => !/\s/.test(String(query || '').trim());

  const ensureStyles = () => {
    if (document.getElementById('pw-predictive-search-price-styles')) return;

    const style = document.createElement('style');
    style.id = 'pw-predictive-search-price-styles';
    style.textContent = `
      .pw-predictive-search__price{
        display:block;
        margin-top:3px;
        color:#171a1d;
        font-size:13px;
        font-weight:850;
        line-height:1.2;
      }
    `;
    document.head.append(style);
  };

  class PaulissenPredictiveSearch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-pw-search-form]');
      this.input = root.querySelector('[data-pw-search-input]');
      this.results = root.querySelector('[data-pw-search-results]');
      this.predictiveSearchUrl = root.dataset.predictiveSearchUrl || '/search/suggest';
      this.searchUrl = root.dataset.searchUrl || '/search';
      this.searchIndexUrl = root.dataset.searchIndexUrl || '/collections/all?view=pw-search-index';
      this.abortController = null;
      this.debounceTimer = null;
      this.cache = new Map();
      this.lastQuery = '';
      this.isSearchPage = window.location.pathname.replace(/\/$/, '') === '/search';

      if (!this.form || !this.input || !this.results) return;

      ensureStyles();

      if (this.isSearchPage) {
        this.results.replaceChildren();
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }

      this.bindEvents();
    }

    bindEvents() {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => {
        if (this.results.childElementCount > 0 && this.input.value.trim().length >= MIN_QUERY_LENGTH) {
          this.open();
        }
      });
      this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      this.results.addEventListener('keydown', (event) => this.onResultsKeydown(event));

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) this.close();
      });
    }

    onInput() {
      window.clearTimeout(this.debounceTimer);
      const query = this.input.value.trim();
      this.lastQuery = query;

      if (query.length < MIN_QUERY_LENGTH) {
        this.abortPendingRequest();
        this.clear();
        return;
      }

      this.debounceTimer = window.setTimeout(() => this.search(query), DEBOUNCE_DELAY);
    }

    onInputKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'ArrowDown' || this.results.hidden) return;
      const firstLink = this.results.querySelector('a');
      if (!firstLink) return;

      event.preventDefault();
      firstLink.focus();
    }

    onResultsKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.input.focus();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = Array.from(this.results.querySelectorAll('a'));
      const currentIndex = links.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + direction + links.length) % links.length;
      links[nextIndex].focus();
    }

    async search(query) {
      const candidates = this.getQueryCandidates(query);
      const cacheKey = candidates.join('\u0001').toLowerCase();
      const useLiveCatalog = this.shouldUseCatalogIndex(query, true);

      if (!useLiveCatalog && this.cache.has(cacheKey)) {
        this.render(this.cache.get(cacheKey), query);
        return;
      }

      this.abortPendingRequest();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.renderStatus('Zoeken…');

      let nativeProducts = [];

      try {
        const nativeResultSets = await Promise.all(
          candidates.map((candidate) => this.fetchProducts(candidate, signal))
        );
        nativeProducts = this.mergeProducts(nativeResultSets);
      } catch (error) {
        if (error.name === 'AbortError') return;
      }

      if (signal.aborted || query !== this.lastQuery) return;

      if (!useLiveCatalog) {
        this.remember(cacheKey, nativeProducts);
        this.render(nativeProducts, query);
        return;
      }

      if (nativeProducts.length > 0) {
        this.render(nativeProducts, query);
      } else {
        this.renderStatus('Typenummer controleren…');
      }

      let indexedProducts = [];
      try {
        const index = await this.loadCatalogIndex();
        indexedProducts = this.findCatalogMatches(index, query);
      } catch (error) {
        // Native resultaten blijven zichtbaar als de aanvullende index tijdelijk niet geladen kan worden.
      }

      if (query !== this.lastQuery) return;

      const products = this.mergeProducts([indexedProducts, nativeProducts]);
      this.render(products, query);
    }

    async fetchProducts(query, signal) {
      const endpoint = this.predictiveSearchUrl.endsWith('.json')
        ? this.predictiveSearchUrl
        : `${this.predictiveSearchUrl}.json`;
      const url = new URL(endpoint, window.location.origin);

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(RESULT_LIMIT));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set(
        'resources[options][fields]',
        'title,vendor,product_type,tag,variants.title,variants.sku,variants.barcode'
      );

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        signal,
        credentials: 'same-origin'
      });

      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const payload = await response.json();
      return payload?.resources?.results?.products || [];
    }

    shouldUseCatalogIndex(query, useAsFallback = false) {
      const normalized = normalizeIdentifier(query);
      if (normalized.length < INDEX_QUERY_LENGTH || !isCompactQuery(query)) return false;

      const hasNumber = /\d/.test(normalized);
      const hasLetter = /[a-z]/.test(normalized);
      return hasNumber || (useAsFallback && hasLetter);
    }

    async loadCatalogIndex() {
      const baseUrl = new URL(this.searchIndexUrl, window.location.origin).toString();
      return this.fetchCatalogIndex(baseUrl);
    }

    async fetchCatalogIndex(baseUrl) {
      const firstPage = await this.fetchCatalogIndexPage(baseUrl, 1);
      const pageCount = Math.max(1, Number(firstPage.pages || 1));
      const remainingRequests = [];

      for (let page = 2; page <= pageCount; page += 1) {
        remainingRequests.push(this.fetchCatalogIndexPage(baseUrl, page));
      }

      const remainingPages = await Promise.all(remainingRequests);
      return [firstPage, ...remainingPages].flatMap((page) => page.products || []);
    }

    async fetchCatalogIndexPage(baseUrl, page) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('page', String(page));
      url.searchParams.set('_pw_search_refresh', String(Date.now()));

      const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) throw new Error(`Search index failed: ${response.status}`);

      const payload = await response.json();
      if (!payload || !Array.isArray(payload.products)) {
        throw new Error('Search index returned invalid data');
      }
      return payload;
    }

    findCatalogMatches(products, query) {
      const needle = normalizeIdentifier(query);
      if (!needle) return [];

      return products
        .filter((product) => !product.hidden)
        .map((product) => {
          const identifierScore = this.getBestIdentifierScore(
            [...(product.skus || []), ...(product.barcodes || [])],
            needle,
            0
          );
          const titleScore = this.getBestIdentifierScore([product.title], needle, 1000);
          const score = Math.min(identifierScore, titleScore);

          if (!Number.isFinite(score)) return null;

          return {
            ...product,
            id: `catalog-${product.id}`,
            image: product.image ? { url: product.image } : null,
            _searchScore: score
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a._searchScore !== b._searchScore) return a._searchScore - b._searchScore;
          if (Boolean(a.available) !== Boolean(b.available)) return a.available ? -1 : 1;
          return String(a.title || '').localeCompare(String(b.title || ''), 'nl');
        })
        .slice(0, RESULT_LIMIT);
    }

    getBestIdentifierScore(values, needle, offset) {
      let best = Number.POSITIVE_INFINITY;

      values.forEach((value) => {
        const haystack = normalizeIdentifier(value);
        if (!haystack) return;

        if (haystack === needle) {
          best = Math.min(best, offset);
          return;
        }

        if (haystack.startsWith(needle)) {
          best = Math.min(best, offset + 100 + haystack.length - needle.length);
          return;
        }

        const matchIndex = haystack.indexOf(needle);
        if (matchIndex !== -1) {
          best = Math.min(best, offset + 200 + matchIndex);
        }
      });

      return best;
    }

    getQueryCandidates(query) {
      const candidates = [query];
      const hasLetters = /[A-Za-z]/.test(query);
      const hasNumbers = /\d/.test(query);

      if (!hasLetters || !hasNumbers) return candidates;

      const collapsed = query.replace(/[\s._\-/]+/g, '');
      if (collapsed !== query && collapsed.length >= MIN_QUERY_LENGTH) {
        candidates.push(collapsed);
      } else {
        const split = query
          .replace(/([A-Za-z])(\d)/g, '$1 $2')
          .replace(/(\d)([A-Za-z])/g, '$1 $2')
          .replace(/\s+/g, ' ')
          .trim();
        if (split !== query && split.length >= MIN_QUERY_LENGTH) candidates.push(split);
      }

      return [...new Set(candidates)].slice(0, 2);
    }

    mergeProducts(resultSets) {
      const seen = new Set();
      const merged = [];

      resultSets.flat().forEach((product) => {
        if (this.isHiddenProduct(product)) return;

        const key = String(product.url || product.id || product.title || '')
          .split('?')[0]
          .replace(/\/$/, '')
          .toLowerCase();

        if (!key || seen.has(key) || merged.length >= RESULT_LIMIT) return;
        seen.add(key);
        merged.push(product);
      });

      return merged;
    }

    isHiddenProduct(product) {
      if (product?.hidden) return true;
      const tags = Array.isArray(product?.tags)
        ? product.tags.map((tag) => String(tag).toLowerCase())
        : [];
      return tags.includes('eol') || tags.includes('product archief');
    }

    remember(key, products) {
      if (this.cache.size >= CACHE_LIMIT) {
        const oldestKey = this.cache.keys().next().value;
        this.cache.delete(oldestKey);
      }
      this.cache.set(key, products);
    }

    render(products, query) {
      this.results.replaceChildren();

      if (products.length === 0) {
        this.results.append(
          this.createStatus('Geen producten gevonden. Probeer een ander deel van het typenummer.'),
          this.createAllResultsLink(query)
        );
        this.open();
        return;
      }

      const list = document.createElement('ul');
      list.className = 'pw-predictive-search__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', 'Voorgestelde producten');

      products.forEach((product) => list.append(this.createProductItem(product)));
      this.results.append(list, this.createAllResultsLink(query));
      this.open();
    }

    createProductItem(product) {
      const item = document.createElement('li');
      item.className = 'pw-predictive-search__item';
      item.setAttribute('role', 'option');

      const link = document.createElement('a');
      link.className = 'pw-predictive-search__link';
      link.href = product.url || this.createSearchUrl(product.title || '');

      const imageUrl = this.getImageUrl(product.image);
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'pw-predictive-search__image';
        image.src = imageUrl;
        image.alt = '';
        image.width = 58;
        image.height = 58;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'pw-predictive-search__image pw-predictive-search__image--placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        link.append(placeholder);
      }

      const text = document.createElement('span');
      text.className = 'pw-predictive-search__text';

      if (product.vendor) {
        const vendor = document.createElement('span');
        vendor.className = 'pw-predictive-search__vendor';
        vendor.textContent = product.vendor;
        text.append(vendor);
      }

      const title = document.createElement('span');
      title.className = 'pw-predictive-search__title';
      title.textContent = product.title || 'Product bekijken';
      text.append(title);

      const priceLabel = this.getPriceLabel(product);
      if (priceLabel) {
        const price = document.createElement('span');
        price.className = 'pw-predictive-search__price';
        price.textContent = priceLabel;
        text.append(price);
      }

      link.append(text);
      item.append(link);
      return item;
    }

    getPriceLabel(product) {
      const raw = product?.price ?? product?.price_min ?? product?.variants?.[0]?.price;
      if (raw === null || raw === undefined || raw === '') return '';

      const text = String(raw).trim();
      if (!text) return '';
      if (/[€$£]/.test(text)) return text;

      const normalized = text.replace(/\s/g, '').replace(',', '.');
      const amount = Number(normalized);
      if (!Number.isFinite(amount)) return text;

      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    }

    createAllResultsLink(query) {
      const link = document.createElement('a');
      link.className = 'pw-predictive-search__all';
      link.href = this.createSearchUrl(query);
      link.textContent = 'Toon meer resultaten';
      return link;
    }

    createSearchUrl(query) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product,page,article');
      url.searchParams.set('options[prefix]', 'last');
      return url.toString();
    }

    getImageUrl(image) {
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.url || image.src || '';
    }

    renderStatus(message) {
      this.results.replaceChildren(this.createStatus(message));
      this.open();
    }

    createStatus(message) {
      const status = document.createElement('div');
      status.className = 'pw-predictive-search__status';
      status.setAttribute('role', 'status');
      status.textContent = message;
      return status;
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    clear() {
      this.results.replaceChildren();
      this.close();
    }

    abortPendingRequest() {
      if (!this.abortController) return;
      this.abortController.abort();
      this.abortController = null;
    }
  }

  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => new PaulissenPredictiveSearch(root));
})();
